import type { CoreConfiguration, PayPalConfiguration } from '@adyen/adyen-web'
import type * as Brink from 'types/vendors/brink'
import { AdyenCheckout, PayPal } from '@adyen/adyen-web'
import { APPLE_PAY_COUNTRY_ERROR, WAREHOUSE_ERROR } from '~/configuration/global.configuration'
import { onAdyenSessionError } from '~/lib/adyen'
import { validateAddress } from '~/models/checkout/validateAddress'
import { useAdyenSupportFunctions } from './useAdyenSupportFunctions'
import { useExpressOrder } from './useExpressOrder'

const logger = useServerLogging('paypal_express')

export async function usePayPal(paymentMethodsResponse: Brink.PaymentMethodsResponseBody) {
  const context = useNuxtApp()
  const cartStore = useCartStore()
  const adyenSupportFunctions = useAdyenSupportFunctions()
  const expressOrder = useExpressOrder()
  expressOrder.setMarket()

  async function createAdvancedFlow(expressErrorArray: Ref<string[]>, amount: { currency: string, value: number }) {
    const config = expressOrder.createConfig(amount, paymentMethodsResponse)
    const checkoutConfiguration: CoreConfiguration = {
      ...config,
      onSubmit: async (state: any, _, actions: any) => {
        try {
          const orderResponse = await context.$payments.createCartOrders()
          expressOrder.saveOrder(orderResponse, false)

          const paymentResponse = await context.$payments.requestPayments({
            orderId: expressOrder.getOrderID(),
            paymentMethod: state.data.paymentMethod,
          })

          const { action, resultCode } = paymentResponse.paymentResult

          const shouldUpdateShippingMethods = expressOrder.shouldUpdateShippingMethods()

          if (shouldUpdateShippingMethods) {
            await expressOrder.fetchShippingMethods()
          }

          const { shippingMethodList, selectedShippingMethod } = await expressOrder.createPayPalShippingMethodList()

          if (action) {
            await context.$payments.updatePayPalExpressOrder({
              orderId: expressOrder.getOrderID(),
              deliveryMethods: shippingMethodList,
              selectedShippingOption: selectedShippingMethod,
            })
          }

          actions.resolve({
            resultCode,
            action,
          })
        }
        catch {
          actions.reject()
        }
      },
      onAdditionalDetails: async (state, _, actions) => {
        const paymentDetailsResponse = await context.$payments.requestPaymentsDetails({
          orderId: expressOrder.getOrderID(),
          details: state.data.details,
        })
        expressOrder.saveOrder(undefined, false)
        actions.resolve({ resultCode: paymentDetailsResponse.paymentResult.resultCode })
      },
      onPaymentCompleted: (result: Adyen.PaymentSessionResponse, component: any) => {
        return adyenSupportFunctions.handleServerResponse(result, component)
      },
      onPaymentFailed: () => {
        cartStore.removeShippingMethodFromCart()
      },
      onError: (error: any) => {
        cartStore.removeShippingMethodFromCart()
        onAdyenSessionError(error) // TODO: WHICH ONE TO USE? FOR BOTH APE AND PAYPAL

        if (error.name !== 'CANCEL') {
          logger.logWarning({
            message: `Apple Pay Express Error: ${error.name}`,
            code: 'error',
            data: JSON.stringify(error),
          })
        }
      },
    }

    const checkout = await AdyenCheckout(checkoutConfiguration)
    return checkout
  }

  async function setUpExpressPayment(checkout: any, expressErrorArray: Ref<string[]>) {
    const paypalConfiguration: PayPalConfiguration = {
      isExpress: true,
      blockPayPalVenmoButton: true,
      blockPayPalCreditButton: true,
      blockPayPalPayLaterButton: true,

      onClick: async () => {
        expressOrder.resetSelectedShippingMethod()
        expressOrder.setMarket()
        expressOrder.setItems()
      },
      onAuthorized: async (data, actions) => {
        const billingData = data.billingAddress
        const deliveryData = data.deliveryAddress
        const currentCountryCode = expressOrder.order.value.market.countryCode
        const countryError = useNuxtApp().$t(APPLE_PAY_COUNTRY_ERROR, { country: currentCountryCode })

        if (!billingData || !deliveryData) {
          actions.reject()
          return
        }

        if (expressOrder.isDifferentWarehouse()) {
          actions.reject()
          expressErrorArray.value.push(useNuxtApp().$t(WAREHOUSE_ERROR))
          return
        }

        const countryCheck = currentCountryCode !== deliveryData.country || currentCountryCode !== billingData.country
        if (countryCheck) {
          if (!expressErrorArray.value.includes(countryError))
            expressErrorArray.value.push(countryError)
          actions.reject()
          return
        }

        expressOrder.setShippingAddress({ authorizedEvent: data.authorizedEvent, address: deliveryData }, false)
        expressOrder.setBillingAddress({ authorizedEvent: data.authorizedEvent, address: billingData }, false)

        // Validate Addresses
        const shippingAddress = expressOrder.getNormalizedShippingAddress()
        const billingAddress = expressOrder.getNormalizedBillingAddress()

        const validShipping = validateAddress(shippingAddress, {
          countryCode: shippingAddress.country || currentCountryCode,
          $t: context.$t,
        })
        const noLastNameValid = !Object.entries(validShipping.errorsPerKey).every(([key, value]) => key === 'lastName' || value.length === 0) && shippingAddress.firstName.includes(' ')

        if (!validShipping.valid && noLastNameValid) {
          expressErrorArray.value.push('Shipping address: ')
          validShipping.errors.forEach((error) => {
            expressErrorArray.value.push(error)
          })
          actions.reject()
          return
        }

        const validBilling = validateAddress(billingAddress, {
          countryCode: billingAddress.country || currentCountryCode,
          $t: context.$t,
        })

        if (!validBilling.valid) {
          expressErrorArray.value.push('Billing address: ')
          validBilling.errors.forEach((error) => {
            expressErrorArray.value.push(error)
          })
          actions.reject()
          return
        }

        const brinkShippingAddress = expressOrder.getBrinkShippingAddress()
        const brinkBillingAddress = expressOrder.getBrinkBillingAddress()
        const email = expressOrder.getBrinkEmail()

        const { shippingMethodList, selectedShippingMethod } = await expressOrder.createPayPalShippingMethodList()

        await context.$payments.updatePayPalExpressOrder({
          orderId: expressOrder.getOrderID(),
          deliveryMethods: shippingMethodList,
          selectedShippingOption: selectedShippingMethod,
          shippingAddress: brinkShippingAddress as Brink.ExpressCartToOrderAddress,
          billingAddress: brinkBillingAddress as Brink.ExpressCartToOrderAddress,
          email,
        })

        actions.resolve()
      },
      onShippingAddressChange: async (data, _, component) => {
        expressErrorArray.value.length = 0
        const currentCountryCode = expressOrder.order.value.market.countryCode
        const isDifferentCountry = currentCountryCode !== data.shippingAddress?.countryCode
        const countryError = useNuxtApp().$t(APPLE_PAY_COUNTRY_ERROR, { country: currentCountryCode })

        if (isDifferentCountry && !expressErrorArray.value.includes(countryError)) {
          expressErrorArray.value.push(countryError)
        }

        const newPaymentData = await context.$payments.updatePayPalExpressOrder({
          orderId: expressOrder.getOrderID(),
          shippingAddress: {
            postalCode: data.shippingAddress.postalCode,
            region: data.shippingAddress.state,
            city: data.shippingAddress.city,
          },
        })

        component.updatePaymentData(newPaymentData.paymentResult.paymentData)
      },
      onShippingOptionsChange: async (data, _, component) => {
        const selectedShippingOption = data.selectedShippingOption
        await expressOrder.setShippingMethod(selectedShippingOption)
        const { shippingMethodList, selectedShippingMethod } = await expressOrder.createPayPalShippingMethodList(selectedShippingOption.id)

        const newPaymentData = await context.$payments.updatePayPalExpressOrder({
          orderId: expressOrder.getOrderID(),
          deliveryMethods: shippingMethodList,
          selectedShippingOption: selectedShippingMethod,
        })

        component.updatePaymentData(newPaymentData.paymentResult.paymentData)
      },
    }

    try {
      const paypal = new PayPal(checkout, paypalConfiguration)
      return paypal
    }
    catch {
      logger.logError({
        message: 'Set up PayPal Express Error',
        code: 'error_on_setup',
        data: JSON.stringify({ config: paypalConfiguration, data: checkout }),
      })
      return null
    }
  }

  return {
    createAdvancedFlow,
    setUpExpressPayment,
  }
}
